import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { Box, Dialog, H1, H5, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { urlBuilder } from '../../Shared/urlBuilder';

export type Props = {
  closeHandler: () => void;
  netWorth: NetWorth;
};

type NetWorth = {
  balance: Money;
  externalSubtotal: Subtotal;
  internalSubtotal: Subtotal;
};

type Subtotal = {
  balance: Money;
  name: string;
  items: Array<NetWorthItem>;
};

type NetWorthItem = {
  balance: Money;
  title: string;
};

export const NetWorthModal: React.FC<Props> = ({ closeHandler, netWorth }) => {
  const netWorthDescription = (
    <Text as="p" css={{ py: 'space.150' }}>
      Your total net worth includes all your Betterment accounts and any connected accounts. Accounts assigned to joint
      or trust goals with multiple owners will factor into every owner's net worth.
      <br />
      <br />
      You can choose which accounts to include in your total net worth. To hide connected accounts or view more details,
      visit your connected account settings.
    </Text>
  );

  return (
    <Dialog overlay="default" open={true} onOpenChange={closeHandler}>
      <Dialog.Content
        css={{
          display: 'flex',
          flexDirection: 'column',
          p: 'space.400',
          overflow: 'auto',
        }}
        size="large"
      >
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Total net worth</H1>
        </Dialog.Title>
        <Box css={{ pb: 'space.400' }}>
          <Dialog.Description asChild={true}>{netWorthDescription}</Dialog.Description>
          <SambaAutoLink to={urlBuilder.appExternalAccounts.absolute()}>Manage connected accounts</SambaAutoLink>
        </Box>
        <Box>
          <Box
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
              pb: 'space.25',
            }}
          >
            <H5
              variant="title100"
              css={{
                fontWeight: 600,
              }}
            >
              {netWorth.internalSubtotal.name} accounts
            </H5>
            <H5
              variant="title100"
              css={{
                fontWeight: 600,
                fontVariantNumeric: 'lining-nums tabular-nums',
              }}
            >
              {formatMoney(netWorth.internalSubtotal.balance)}
            </H5>
          </Box>

          {netWorth.internalSubtotal.items.map(row => (
            <Box
              key={`internal-${row.title}-${row.balance.cents}`}
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
                gap: 'space.50',
                py: 'space.25',
              }}
            >
              <Text>{row.title}</Text>
              <Text
                variant="text100"
                css={{
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
              >
                {formatMoney(row.balance)}
              </Text>
            </Box>
          ))}

          <Box css={{ py: 'space.200' }}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
                gap: 'space.50',
                pb: 'space.25',
              }}
            >
              <H5
                variant="title100"
                css={{
                  fontWeight: 600,
                }}
              >
                {netWorth.externalSubtotal.name} accounts
              </H5>
              <H5
                variant="title100"
                css={{
                  fontWeight: 600,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
              >
                {formatMoney(netWorth.externalSubtotal.balance)}
              </H5>
            </Box>

            {netWorth.externalSubtotal.items.map(row => (
              <Box
                key={`external-${row.title}-${row.balance.cents}`}
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignSelf: 'stretch',
                  gap: 'space.50',
                  py: 'space.25',
                }}
              >
                <Text>{row.title}</Text>
                <Text
                  variant="text100"
                  css={{
                    fontVariantNumeric: 'lining-nums tabular-nums',
                  }}
                >
                  {formatMoney(row.balance)}
                </Text>
              </Box>
            ))}
          </Box>

          <Separator />

          <Box css={{ pt: 'space.200' }}>
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignSelf: 'stretch',
              }}
            >
              <H5 variant="title100">Total net worth</H5>
              <H5
                variant="title100"
                css={{
                  fontWeight: 600,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}
              >
                {formatMoney(netWorth.balance)}
              </H5>
            </Box>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
